import dayjs, { type Dayjs } from 'dayjs';

import { EDateFormat } from './date-format';

export enum EDateRangePreset {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last7Days',
  LAST_14_DAYS = 'last14Days',
  LAST_30_DAYS = 'last30Days',
  LAST_90_DAYS = 'last90Days',
  LAST_365_DAYS = 'last365Days',
  THIS_WEEK = 'thisWeek',
  LAST_WEEK = 'lastWeek',
  THIS_MONTH = 'thisMonth',
  LAST_MONTH = 'lastMonth',
  THIS_QUARTER = 'thisQuarter',
  LAST_QUARTER = 'lastQuarter',
  THIS_YEAR = 'thisYear',
  LAST_YEAR = 'lastYear',
  THIS_YEAR_Q1 = 'thisYearQ1',
  THIS_YEAR_Q2 = 'thisYearQ2',
  THIS_YEAR_Q3 = 'thisYearQ3',
  THIS_YEAR_Q4 = 'thisYearQ4',
  LAST_YEAR_Q1 = 'lastYearQ1',
  LAST_YEAR_Q2 = 'lastYearQ2',
  LAST_YEAR_Q3 = 'lastYearQ3',
  LAST_YEAR_Q4 = 'lastYearQ4',
  THIS_YEAR_JANUARY = 'thisYearJanuary',
  THIS_YEAR_FEBRUARY = 'thisYearFebruary',
  THIS_YEAR_MARCH = 'thisYearMarch',
  THIS_YEAR_APRIL = 'thisYearApril',
  THIS_YEAR_MAY = 'thisYearMay',
  THIS_YEAR_JUNE = 'thisYearJune',
  THIS_YEAR_JULY = 'thisYearJuly',
  THIS_YEAR_AUGUST = 'thisYearAugust',
  THIS_YEAR_SEPTEMBER = 'thisYearSeptember',
  THIS_YEAR_OCTOBER = 'thisYearOctober',
  THIS_YEAR_NOVEMBER = 'thisYearNovember',
  THIS_YEAR_DECEMBER = 'thisYearDecember',
  CUSTOM = 'custom',
}

export type TDateRangePreset = Exclude<
  EDateRangePreset,
  EDateRangePreset.CUSTOM
>;

export interface IDateRange {
  from: Dayjs;
  to: Dayjs;
}

export interface IIsoDateRange {
  from: string;
  to: string;
}

const MONTHS_IN_QUARTER = 3;

const DAYS_IN_WEEK = 7;

export const ROLLING_DATE_RANGE_PRESETS: readonly TDateRangePreset[] = [
  EDateRangePreset.TODAY,
  EDateRangePreset.YESTERDAY,
  EDateRangePreset.LAST_7_DAYS,
  EDateRangePreset.LAST_14_DAYS,
  EDateRangePreset.LAST_30_DAYS,
  EDateRangePreset.LAST_90_DAYS,
  EDateRangePreset.LAST_365_DAYS,
];

export const CALENDAR_DATE_RANGE_PRESETS: readonly TDateRangePreset[] = [
  EDateRangePreset.THIS_WEEK,
  EDateRangePreset.LAST_WEEK,
  EDateRangePreset.THIS_MONTH,
  EDateRangePreset.LAST_MONTH,
  EDateRangePreset.THIS_QUARTER,
  EDateRangePreset.LAST_QUARTER,
  EDateRangePreset.THIS_YEAR,
  EDateRangePreset.LAST_YEAR,
];

export const QUARTER_DATE_RANGE_PRESETS: readonly TDateRangePreset[] = [
  EDateRangePreset.THIS_YEAR_Q1,
  EDateRangePreset.THIS_YEAR_Q2,
  EDateRangePreset.THIS_YEAR_Q3,
  EDateRangePreset.THIS_YEAR_Q4,
  EDateRangePreset.LAST_YEAR_Q1,
  EDateRangePreset.LAST_YEAR_Q2,
  EDateRangePreset.LAST_YEAR_Q3,
  EDateRangePreset.LAST_YEAR_Q4,
];

export const MONTH_DATE_RANGE_PRESETS: readonly TDateRangePreset[] = [
  EDateRangePreset.THIS_YEAR_JANUARY,
  EDateRangePreset.THIS_YEAR_FEBRUARY,
  EDateRangePreset.THIS_YEAR_MARCH,
  EDateRangePreset.THIS_YEAR_APRIL,
  EDateRangePreset.THIS_YEAR_MAY,
  EDateRangePreset.THIS_YEAR_JUNE,
  EDateRangePreset.THIS_YEAR_JULY,
  EDateRangePreset.THIS_YEAR_AUGUST,
  EDateRangePreset.THIS_YEAR_SEPTEMBER,
  EDateRangePreset.THIS_YEAR_OCTOBER,
  EDateRangePreset.THIS_YEAR_NOVEMBER,
  EDateRangePreset.THIS_YEAR_DECEMBER,
];

export const DEFAULT_DATE_RANGE_PRESETS: readonly TDateRangePreset[] = [
  ...ROLLING_DATE_RANGE_PRESETS,
  ...CALENDAR_DATE_RANGE_PRESETS,
];

export const DATE_RANGE_CUSTOM_LABEL = 'Custom range';

const STATIC_LABEL: Partial<Record<TDateRangePreset, string>> = {
  [EDateRangePreset.TODAY]: 'Today',
  [EDateRangePreset.YESTERDAY]: 'Yesterday',
  [EDateRangePreset.LAST_7_DAYS]: 'Last 7 days',
  [EDateRangePreset.LAST_14_DAYS]: 'Last 14 days',
  [EDateRangePreset.LAST_30_DAYS]: 'Last 30 days',
  [EDateRangePreset.LAST_90_DAYS]: 'Last 90 days',
  [EDateRangePreset.LAST_365_DAYS]: 'Last 365 days',
  [EDateRangePreset.THIS_WEEK]: 'This week',
  [EDateRangePreset.LAST_WEEK]: 'Last week',
  [EDateRangePreset.THIS_MONTH]: 'This month',
  [EDateRangePreset.LAST_MONTH]: 'Last month',
  [EDateRangePreset.THIS_QUARTER]: 'This quarter',
  [EDateRangePreset.LAST_QUARTER]: 'Last quarter',
  [EDateRangePreset.THIS_YEAR]: 'This year',
  [EDateRangePreset.LAST_YEAR]: 'Last year',
};

const ROLLING_DAYS: Partial<Record<TDateRangePreset, number>> = {
  [EDateRangePreset.LAST_7_DAYS]: 7,
  [EDateRangePreset.LAST_14_DAYS]: 14,
  [EDateRangePreset.LAST_30_DAYS]: 30,
  [EDateRangePreset.LAST_90_DAYS]: 90,
  [EDateRangePreset.LAST_365_DAYS]: 365,
};

export const getWeekStart = (date: Dayjs): Dayjs =>
  date
    .startOf('day')
    .subtract((date.day() + DAYS_IN_WEEK - 1) % DAYS_IN_WEEK, 'day');

export const getQuarterStart = (date: Dayjs): Dayjs =>
  date
    .startOf('month')
    .month(Math.floor(date.month() / MONTHS_IN_QUARTER) * MONTHS_IN_QUARTER);

export const getQuarterLabel = (date: Dayjs): string =>
  `Q${Math.floor(date.month() / MONTHS_IN_QUARTER) + 1} ${date.year()}`;

const unitRange = (
  from: Dayjs,
  unit: 'day' | 'week' | 'month' | 'year',
): IDateRange => ({
  from,
  to: from.add(1, unit),
});

const quarterRange = (from: Dayjs): IDateRange => ({
  from,
  to: from.add(MONTHS_IN_QUARTER, 'month'),
});

const fixedQuarterRange = (
  now: Dayjs,
  yearsAgo: number,
  quarterIndex: number,
): IDateRange =>
  quarterRange(
    now
      .startOf('year')
      .subtract(yearsAgo, 'year')
      .add(quarterIndex * MONTHS_IN_QUARTER, 'month'),
  );

const resolveCalendarRange = (
  preset: TDateRangePreset,
  now: Dayjs,
): IDateRange => {
  const quarterIndex: number = QUARTER_DATE_RANGE_PRESETS.indexOf(preset);

  if (quarterIndex !== -1) {
    return fixedQuarterRange(
      now,
      Math.floor(quarterIndex / 4),
      quarterIndex % 4,
    );
  }

  const monthIndex: number = MONTH_DATE_RANGE_PRESETS.indexOf(preset);

  if (monthIndex !== -1) {
    return unitRange(now.startOf('year').month(monthIndex), 'month');
  }

  switch (preset) {
    case EDateRangePreset.TODAY:
      return unitRange(now.startOf('day'), 'day');
    case EDateRangePreset.YESTERDAY:
      return unitRange(now.startOf('day').subtract(1, 'day'), 'day');
    case EDateRangePreset.THIS_WEEK:
      return unitRange(getWeekStart(now), 'week');
    case EDateRangePreset.LAST_WEEK:
      return unitRange(getWeekStart(now).subtract(1, 'week'), 'week');
    case EDateRangePreset.THIS_MONTH:
      return unitRange(now.startOf('month'), 'month');
    case EDateRangePreset.LAST_MONTH:
      return unitRange(now.startOf('month').subtract(1, 'month'), 'month');
    case EDateRangePreset.THIS_QUARTER:
      return quarterRange(getQuarterStart(now));
    case EDateRangePreset.LAST_QUARTER:
      return quarterRange(
        getQuarterStart(now).subtract(MONTHS_IN_QUARTER, 'month'),
      );
    case EDateRangePreset.THIS_YEAR:
      return unitRange(now.startOf('year'), 'year');
    case EDateRangePreset.LAST_YEAR:
      return unitRange(now.startOf('year').subtract(1, 'year'), 'year');
    default:
      return unitRange(now.startOf('day'), 'day');
  }
};

export const resolveDateRange = (
  preset: TDateRangePreset,
  now: Dayjs = dayjs(),
  endBefore?: Dayjs,
): IDateRange => {
  const rollingDays: number | undefined = ROLLING_DAYS[preset];

  if (rollingDays !== undefined) {
    const to: Dayjs = endBefore ?? now.startOf('day').add(1, 'day');
    return { from: to.subtract(rollingDays, 'day'), to };
  }

  const range: IDateRange = resolveCalendarRange(preset, now);

  if (endBefore && range.to.isAfter(endBefore)) {
    return { from: range.from, to: endBefore };
  }

  return range;
};

export const getDateRangeDays = (range: IDateRange): number =>
  Math.round(range.to.diff(range.from, 'day', true));

export const isDateRangeEmpty = (range: IDateRange): boolean =>
  !range.from.isBefore(range.to);

export const isSameDateRange = (a: IDateRange, b: IDateRange): boolean =>
  a.from.isSame(b.from) && a.to.isSame(b.to);

export const findDateRangePreset = (
  range: IDateRange,
  presets: readonly TDateRangePreset[],
  now: Dayjs = dayjs(),
  endBefore?: Dayjs,
): EDateRangePreset =>
  presets.find((preset: TDateRangePreset): boolean =>
    isSameDateRange(resolveDateRange(preset, now, endBefore), range),
  ) ?? EDateRangePreset.CUSTOM;

export const getDateRangePresetLabel = (
  preset: EDateRangePreset,
  now: Dayjs = dayjs(),
): string => {
  if (preset === EDateRangePreset.CUSTOM) {
    return DATE_RANGE_CUSTOM_LABEL;
  }

  if (QUARTER_DATE_RANGE_PRESETS.includes(preset)) {
    return getQuarterLabel(resolveCalendarRange(preset, now).from);
  }

  if (MONTH_DATE_RANGE_PRESETS.includes(preset)) {
    return resolveCalendarRange(preset, now).from.format(EDateFormat.MONTH);
  }

  return STATIC_LABEL[preset] ?? preset;
};

const isWholeUnits = (
  range: IDateRange,
  unit: 'week' | 'month' | 'year',
  count: number,
): boolean =>
  range.from.isSame(
    unit === 'week' ? getWeekStart(range.from) : range.from.startOf(unit),
  ) && range.to.isSame(range.from.add(count, unit));

export const getPreviousDateRange = (range: IDateRange): IDateRange => {
  if (isWholeUnits(range, 'year', 1)) {
    return { from: range.from.subtract(1, 'year'), to: range.from };
  }

  if (
    isWholeUnits(range, 'month', MONTHS_IN_QUARTER) &&
    range.from.month() % MONTHS_IN_QUARTER === 0
  ) {
    return {
      from: range.from.subtract(MONTHS_IN_QUARTER, 'month'),
      to: range.from,
    };
  }

  if (isWholeUnits(range, 'month', 1)) {
    return { from: range.from.subtract(1, 'month'), to: range.from };
  }

  if (isWholeUnits(range, 'week', 1)) {
    return { from: range.from.subtract(1, 'week'), to: range.from };
  }

  return {
    from: range.from.subtract(getDateRangeDays(range), 'day'),
    to: range.from,
  };
};

export const isDateInRange = (date: Dayjs, range: IDateRange): boolean =>
  !date.isBefore(range.from) && date.isBefore(range.to);

export const fromPickerValue = (start: Dayjs, end: Dayjs): IDateRange => ({
  from: start.startOf('day'),
  to: end.startOf('day').add(1, 'day'),
});

export const toPickerValue = (range: IDateRange): [Dayjs, Dayjs] => [
  range.from,
  range.to.subtract(1, 'day'),
];

export const toIsoDateRange = (range: IDateRange): IIsoDateRange => ({
  from: range.from.toISOString(),
  to: range.to.toISOString(),
});

export const fromIsoDateRange = (range: IIsoDateRange): IDateRange => ({
  from: dayjs(range.from),
  to: dayjs(range.to),
});

export const toInclusiveDates = (range: IDateRange): [string, string] => [
  range.from.format(EDateFormat.DATE),
  range.to.subtract(1, 'day').format(EDateFormat.DATE),
];

export const fromInclusiveDates = (from: string, to: string): IDateRange =>
  fromPickerValue(dayjs(from), dayjs(to));

export const formatDateRange = (range: IDateRange): string => {
  const [from, to]: [string, string] = toInclusiveDates(range);

  return from === to ? from : `${from} – ${to}`;
};
